import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
// Add CSS for hiding webkit scrollbar
const hideScrollbarStyle = `
  .hide-webkit-scrollbar::-webkit-scrollbar {
    display: none;
  }
`;
// Inject CSS into document head if not already present
if (typeof document !== 'undefined' && !document.getElementById('hide-scrollbar-style')) {
    const style = document.createElement('style');
    style.id = 'hide-scrollbar-style';
    style.textContent = hideScrollbarStyle;
    document.head.appendChild(style);
}
const FeatureTemplates = ({ templates, selectedTemplateId, onSelect, isReadonly = false, styles, }) => {
    const containerRef = React.useRef(null);
    const [canScrollLeft, setCanScrollLeft] = React.useState(false);
    const [canScrollRight, setCanScrollRight] = React.useState(false);
    const checkScrollability = () => {
        if (containerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
        }
    };
    React.useEffect(() => {
        // Check scrollability after layout is complete
        const timeoutId = setTimeout(() => {
            checkScrollability();
        }, 100);
        const container = containerRef.current;
        if (container) {
            container.addEventListener('scroll', checkScrollability);
            // Also check on resize
            const resizeObserver = new ResizeObserver(() => {
                checkScrollability();
            });
            resizeObserver.observe(container);
            return () => {
                container.removeEventListener('scroll', checkScrollability);
                resizeObserver.disconnect();
                clearTimeout(timeoutId);
            };
        }
        return () => clearTimeout(timeoutId);
    }, [templates]);
    const scrollLeft = () => {
        if (containerRef.current) {
            containerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
        }
    };
    const scrollRight = () => {
        if (containerRef.current) {
            containerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
        }
    };
    const containerStyle = {
        ...styles.featureTemplatesContainer,
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitScrollbar: 'none',
    };
    return (_jsxs("div", { style: { position: 'relative', width: '100%' }, children: [canScrollLeft && (_jsx("button", { onClick: scrollLeft, style: {
                    position: 'absolute',
                    left: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 30000,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    border: '1px solid #D5DCE4',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s ease',
                }, onMouseEnter: (e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(14, 73, 122, 0.1)';
                }, onMouseLeave: (e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                }, children: _jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "#0E497A", strokeWidth: "2", children: _jsx("polyline", { points: "15,18 9,12 15,6" }) }) })), canScrollRight && (_jsx("button", { onClick: scrollRight, style: {
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 30000,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    border: '1px solid #D5DCE4',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s ease',
                }, onMouseEnter: (e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(14, 73, 122, 0.1)';
                }, onMouseLeave: (e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                }, children: _jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "#0E497A", strokeWidth: "2", children: _jsx("polyline", { points: "9,18 15,12 9,6" }) }) })), _jsx("div", { ref: containerRef, className: "hide-webkit-scrollbar", style: {
                    ...containerStyle,
                    WebkitOverflowScrolling: 'touch',
                }, children: templates.map((template) => {
                    const isSelected = template.id === selectedTemplateId;
                    const hasSelection = selectedTemplateId !== null;
                    const isDisabled = hasSelection && !isSelected;
                    const cardStyle = {
                        ...styles.featureTemplateCard,
                        cursor: (isReadonly || isDisabled) ? 'default' : 'pointer',
                        opacity: (isReadonly && !isSelected) || isDisabled ? 0.5 : 1,
                        pointerEvents: (isReadonly && !isDisabled) || isDisabled ? 'none' : 'auto',
                    };
                    const titleStyle = {
                        ...styles.featureTemplateTitle,
                        ...(isSelected ? { color: '#fff' } : {}),
                    };
                    const descriptionStyle = {
                        ...styles.featureTemplateDescription,
                        ...(isSelected ? { color: '#fff' } : {}),
                    };
                    const contentStyle = {
                        ...styles.featureTemplateContent,
                        ...(isSelected ? { backgroundColor: '#0E497A' } : {}),
                    };
                    return (_jsxs("div", { style: cardStyle, onClick: () => !isReadonly && !isDisabled && onSelect?.(template), onMouseEnter: () => !isReadonly && !isDisabled && typeof styles.onHover === 'function' && styles.onHover(template.id), onMouseLeave: () => !isReadonly && !isDisabled && typeof styles.onHoverEnd === 'function' && styles.onHoverEnd(template.id), children: [_jsx("div", { style: { position: 'relative', zIndex: 20000 }, children: template.icon ? (_jsx("img", { src: template.icon, alt: template.title, style: styles.featureTemplateIcon })) : (_jsx("div", { style: {
                                        width: '70px',
                                        height: '70px',
                                        backgroundColor: '#F1F8FD',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '24px',
                                        color: '#0E497A',
                                        zIndex: 20000,
                                    }, children: template.id === 'question-items' ? '?' : '+' })) }), _jsxs("div", { style: contentStyle, children: [_jsx("div", { style: titleStyle, children: template.title }), _jsx("div", { style: descriptionStyle, children: template.description })] })] }, template.id));
                }) })] }));
};
export default FeatureTemplates;
